import { AttachFile, CheckCircle } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Suspense, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ProcessExecutionAPI } from "../../../../api";
import { HumanReadableTimestamp } from "../../../../components";
import DynamicIcon from "../../../../components/core/DynamicIcon";
import Dialog from "../../../../components/Dialog";
import { TaskExecution } from "../../../../features/task";
import Notification, { NotificationType } from "../../../../model/Notification";
import { ProcessExecutionTracking } from "../../../../model/NotificationEvent";
import { ExecutionState } from "../../../../model/Process";
import { ProcessExecution } from "../../../../model/ProcessExecution";
import messages from "../../../process/messages";
import TaskExecutionMiniTracker from "../../../task/components/execution/TaskExecutionMiniTracker";

const TITLES: {
  [key in NotificationType]: string;
} = {
  [NotificationType.TASK]: "Task Completed",
  [NotificationType.PROCESS]: "Process Completed",
  [NotificationType.ORGANIZATION]: "Organization Completed",
  [NotificationType.GENERAL]: "Notification Details",
};

const CONFIRM_TEXT: {
  [key in NotificationType]: string;
} = {
  [NotificationType.TASK]: "Go to Task",
  [NotificationType.PROCESS]: "View More",
  [NotificationType.ORGANIZATION]: "Go to Settings",
  [NotificationType.GENERAL]: "Go to General",
};

export const TaskCompletedNotificationDetails = ({
  notification,
  handleGoTo,
}: {
  notification: Notification | undefined;
  handleGoTo?: () => void;
}) => {
  const processExecutionId = notification?.data?.process_execution_id;
  const taskId = notification?.data?.task_id;

  const [taskExecution, setTaskExecution] = useState<TaskExecution>();
  const [notificationEvents, setNotificationEvents] = useState<
    ProcessExecutionTracking[]
  >([]);
  const [processExecution, setProcessExecution] = useState<ProcessExecution>();

  const intl = useIntl();
  const theme = useTheme();

  const processName = intl.formatMessage(messages.execution.props);

  useEffect(() => {
    ProcessExecutionAPI.getTaskExecution(
      processExecutionId as string,
      taskId as string,
    ).then((result) => {
      setTaskExecution(result);
    });
  }, [processExecutionId, taskId]);

  useEffect(() => {
    if (!processExecutionId) return;

    ProcessExecutionAPI.getDto(processExecutionId as string).then((result) => {
      setProcessExecution(result);
    });
  }, [processExecutionId]);

  useEffect(() => {
    if (!taskId) return;

    ProcessExecutionAPI.getTaskTrackingInfo(
      processExecutionId as string,
      taskId as string,
    ).then((tracking) => {
      const useEvents = [];
      for (const track of tracking) {
        const processExecutionTracking = {
          timestamp: new Date(track.timestamp),
          state: track.eventType,
        };
        useEvents.push(processExecutionTracking);
      }
      setNotificationEvents(useEvents);
    });
  }, [processExecutionId, taskId]);

  return (
    <Box>
      <Typography>
        Someone has completed a task that was set to notify you! You can view
        the task details, or continue along with the {processName}.
      </Typography>
      <Card
        sx={{
          padding: 2,
          marginTop: 2,
        }}
      >
        <Box>
          <Box display={"flex"} gap={2} alignItems={"center"}>
            <IconButton size="small" sx={{ color: "black" }}>
              {processExecution?.process_icon ? (
                <Suspense fallback={<AttachFile sx={{ fontSize: 24 }} />}>
                  <DynamicIcon name={processExecution?.process_icon} />
                </Suspense>
              ) : (
                <AttachFile sx={{ fontSize: 24 }} />
              )}
            </IconButton>
            <Typography variant="h6" fontWeight={"bold"} flex={1}>
              {processExecution?.name}
            </Typography>

            {processExecution?.updated_at && (
              <Typography variant="body1">
                Updated{" "}
                {processExecution?.updated_at && (
                  <HumanReadableTimestamp
                    timestamp={processExecution?.updated_at}
                  />
                )}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
      <Card
        sx={{
          padding: 2,
          marginTop: 2,
        }}
      >
        <Box>
          <Box display={"flex"} gap={2} alignItems={"center"}>
            <IconButton size="medium" sx={{ color: "black" }}>
              <CheckCircle sx={{ fontSize: 40 }} />
            </IconButton>
            <Box flex={1}>
              <Typography variant="h4" fontWeight={"bold"}>
                {taskExecution?.name}
              </Typography>
              <Typography variant="body1">
                Completed{" "}
                {taskExecution && taskExecution.completed_at && (
                  <HumanReadableTimestamp
                    timestamp={taskExecution?.completed_at}
                  />
                )}
              </Typography>
            </Box>
            <Box>
              <Button variant="outlined" color="primary" onClick={handleGoTo}>
                View {processName}
              </Button>
            </Box>
          </Box>
          <Box display={"flex"} justifyContent={"center"} gap={2} mt={2}>
            <TaskExecutionMiniTracker
              notification_events={notificationEvents}
              color={theme.palette.primary}
            />
          </Box>
        </Box>
      </Card>
      {processExecution?.state == ExecutionState.Completed ? (
        <Alert
          sx={{
            marginTop: 2,
          }}
          severity="success"
        >
          <Box>
            <Box display={"flex"} gap={2} alignItems={"center"}>
              <Typography variant="body1">
                This {processName} has been completed, and there is nothing left
                to do!
              </Typography>
            </Box>
          </Box>
        </Alert>
      ) : (
        <Alert
          sx={{
            marginTop: 2,
          }}
          severity="info"
        >
          <Box>
            <Typography variant="body1">
              This {processName} still has tasks pending
            </Typography>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

const NotificationDetailsDialog = ({
  notification,
  open,
  handleGoTo,
  handleClose,
  title,
}: {
  notification: Notification | undefined;
  open: boolean;
  handleGoTo: () => void;
  handleClose: () => void;
  title?: string;
}) => {
  return (
    <Dialog
      open={open}
      handleCancel={handleClose}
      title={
        title ||
        (notification?.type
          ? TITLES[notification?.type]
          : "Notification Details")
      }
      cancelText={"Close"}
      handleConfirm={async () => {
        handleGoTo();
      }}
      confirmText={
        (notification?.type ? CONFIRM_TEXT[notification?.type] : "Go to") || ""
      }
      fullWidth={true}
    >
      {notification && (
        <>
          <Box>
            {notification.type == NotificationType.TASK && (
              <TaskCompletedNotificationDetails
                notification={notification}
                handleGoTo={handleGoTo}
              />
            )}
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default NotificationDetailsDialog;
